/**
 * Affiliate-Referral-Tracking (Client-Seite).
 *
 * Flow:
 *  1. Besucher landet auf immokompass.app/?ref=<code>.
 *  2. captureRefFromUrl() liest den Code, speichert ihn (localStorage,
 *     mit Timestamp für ein Attribution-Window) und feuert einmalig pro
 *     Browser-Session einen anonymen Klick-Event an /api/affiliate-track.
 *  3. Nach (erstem) Login liest useAffiliateAttribution() den Code und
 *     ordnet den User per /api/affiliate-attribute dem Partner zu
 *     (first-touch, serverseitig idempotent).
 *
 * Der Code bleibt nach der Attribution erhalten bis das Window abläuft —
 * schadet nicht, da der Server-Endpoint idempotent ist.
 */

const REF_KEY = 'immokompass:affiliateRef';
const REF_TS_KEY = 'immokompass:affiliateRefTs';
const CLICK_FIRED_KEY = 'immokompass:affiliateClickFired';

// Attribution-Window: 60 Tage zwischen Klick und Signup.
const ATTRIBUTION_WINDOW_MS = 60 * 24 * 60 * 60 * 1000;

const CODE_RE = /^[A-Za-z0-9_-]{1,64}$/;

function safeLocalGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* Storage disabled (Inkognito strict) — Tracking ist best-effort */
  }
}

function safeLocalRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * Liest einen gültigen, nicht abgelaufenen Referral-Code aus dem Storage.
 * Räumt abgelaufene Einträge auf.
 */
export function getStoredRef(): string | null {
  const code = safeLocalGet(REF_KEY);
  if (!code || !CODE_RE.test(code)) return null;
  const tsRaw = safeLocalGet(REF_TS_KEY);
  const ts = tsRaw ? Number(tsRaw) : 0;
  if (!Number.isFinite(ts) || ts <= 0 || Date.now() - ts > ATTRIBUTION_WINDOW_MS) {
    // Abgelaufen → aufräumen.
    clearStoredRef();
    return null;
  }
  return code;
}

export function clearStoredRef(): void {
  safeLocalRemove(REF_KEY);
  safeLocalRemove(REF_TS_KEY);
}

/**
 * Beim App-Start aufrufen: liest `?ref=<code>` aus der URL, persistiert
 * ihn (first-touch — überschreibt einen bestehenden Code NICHT) und
 * feuert einen anonymen Klick-Event (max. 1× pro Browser-Session).
 *
 * Entfernt den `ref`-Param NICHT aus der URL — das überlassen wir dem
 * Aufrufer (optional via history.replaceState), damit React-Router-State
 * nicht durcheinandergerät.
 */
export function captureRefFromUrl(): void {
  if (typeof window === 'undefined') return;
  let code: string | null = null;
  try {
    code = new URLSearchParams(window.location.search).get('ref');
  } catch {
    return;
  }
  if (!code) return;
  code = code.trim();
  if (!CODE_RE.test(code)) return;

  // First-touch: nur setzen, wenn noch kein gültiger Code gespeichert ist.
  const existing = getStoredRef();
  if (!existing) {
    safeLocalSet(REF_KEY, code);
    safeLocalSet(REF_TS_KEY, String(Date.now()));
  }

  // Klick-Event genau 1× pro Browser-Session feuern (sessionStorage-Flag),
  // damit Reloads / SPA-Navigation nicht jeden Page-View als Klick zählen.
  let alreadyFired = false;
  try {
    alreadyFired = sessionStorage.getItem(CLICK_FIRED_KEY) === code;
  } catch {
    /* ignore */
  }
  if (!alreadyFired) {
    try {
      sessionStorage.setItem(CLICK_FIRED_KEY, code);
    } catch {
      /* ignore */
    }
    // Fire-and-forget — kein await, Fehler ignorieren.
    void fetch('/api/affiliate-track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
      keepalive: true,
    }).catch(() => {
      /* best-effort */
    });
  }
}
